class ContactFormElement extends HTMLElement {
  connectedCallback() {
    this._form = this.querySelector('form');
    this._banner = this.querySelector('[data-popup="contacts-message"]');
    this._errorEl = this.querySelector('[data-form-error]');
    this._resetBtn = this.querySelector('[data-form-reset]');

    if (!this._form) return;

    // ① Capture phase — fires before any other listener, always blocks native reload.
    this._form.addEventListener('submit', (e) => e.preventDefault(), { capture: true });

    // ② Shopify's invisible hCaptcha validates on the submit event and then calls
    //    form.submit() programmatically with the token already filled.
    //    We replace that method so we do an AJAX fetch instead.
    this._form.submit = () => {
      if (this._isFormReady()) this._fetchSubmit();
    };

    // ③ Bubble phase — fallback for stores without hCaptcha where nobody will
    //    call form.submit() after the submit event.
    //    Runs after TS validation; if TS called stopImmediatePropagation this
    //    won't fire (which is correct — TS found errors).
    this._form.addEventListener('submit', () => {
      if (this._hasCaptchaField()) return; // captcha case handled via form.submit override
      if (this._isFormReady()) this._fetchSubmit();
    });

    this._resetBtn?.addEventListener('click', this._onReset.bind(this));
  }

  // True when a Shopify-injected hCaptcha response field is present in the form.
  _hasCaptchaField() {
    return !!this._form.querySelector('[name="h-captcha-response"]');
  }

  // All [data-required] fields have values, checkbox is checked,
  // and TS has not left any visible per-field error messages.
  _isFormReady() {
    for (const wrapper of this._form.querySelectorAll('[data-required]')) {
      const checkbox = wrapper.querySelector('input[type="checkbox"]');
      if (checkbox) {
        if (!checkbox.checked) return false;
        continue;
      }
      const field = wrapper.querySelector('input, textarea');
      if (!field || !field.value.trim()) return false;
    }
    return !Array.from(this._form.querySelectorAll('.input-message'))
      .some((el) => el.textContent.trim().length > 0);
  }

  _submitToGoogle() {
    const GOOGLE_ACTION =
      'https://docs.google.com/forms/d/e/1FAIpQLSd4RQLycjQHYPNBhKYDoRs9cUG7XeiqkBCcX7QcmSRyrZyngA/formResponse';
    const FIELD_MAP = {
      'contact[name]': 'entry.1265221820',
      'contact[email]': 'entry.887490772',
      'contact[Dream title]': 'entry.1987822191',
      'contact[Dream date]': 'entry.990597267',
      'contact[body]': 'entry.227083606',
    };
    const fd = new FormData();
    const payload = {};
    for (const [shopifyName, gEntry] of Object.entries(FIELD_MAP)) {
      const input = this._form.querySelector(`[name="${shopifyName}"]`);
      if (input?.value) {
        fd.append(gEntry, input.value);
        payload[gEntry] = input.value;
      }
    }
    console.log('[Google Form] submitting payload:', payload);
    fetch(GOOGLE_ACTION, { method: 'POST', mode: 'no-cors', body: fd })
      .then(() => console.log('[Google Form] sent (no-cors — no status available)'))
      .catch((err) => console.warn('[Google Form] fetch error:', err));
  }

  async _fetchSubmit() {
    this._submitToGoogle();
    this._hideFetchError();

    try {
      const res = await fetch(this._form.action, {
        method: 'POST',
        body: new FormData(this._form),
      });

      // On success Shopify redirects to ?contact_posted=true (or ?contact%5Bposted_successfully%5D=true).
      // fetch follows the redirect automatically, so res.url carries the indicator.
      const text = await res.text();
      const ok =
        res.url.includes('contact_posted=true') ||
        res.url.includes('posted_successfully') ||
        text.includes('contact_posted=true') ||
        text.includes('posted_successfully');

      if (ok) {
        this._showSuccess();
      } else {
        this._form.reset();
        this._showFetchError();
      }
    } catch {
      this._form.reset();
      this._showFetchError();
    }
  }

  _showSuccess() {
    this._banner?.classList.add('is-show');
  }

  _showFetchError() {
    if (!this._errorEl) return;
    this._errorEl.hidden = false;
  }

  _hideFetchError() {
    if (!this._errorEl) return;
    this._errorEl.hidden = true;
  }

  _onReset() {
    this._form.reset();
    this._banner?.classList.remove('is-show');
    this._hideFetchError();
    // Dispatch input events so TS clears per-field error states.
    this._form.querySelectorAll('input, textarea').forEach((el) => {
      el.dispatchEvent(new Event('input', { bubbles: true }));
    });
  }
}

if (!customElements.get('contact-form')) {
  customElements.define('contact-form', ContactFormElement);
}
